import React, { useRef, useEffect, useState } from "react";

export default function VerticalReveal({ slides = [], navHeight = 0, spacerExtra = 0 }) {
    const wrapperRef = useRef(null);
    const rafRef = useRef(null);

    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper || slides.length === 0) return;

        const updateHeight = () => {
            wrapper.style.height = `${slides.length * window.innerHeight + spacerExtra}px`;
        };

        updateHeight();
        window.addEventListener("resize", updateHeight);

        const onScroll = () => {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(() => {
                const rect = wrapper.getBoundingClientRect();
                const vh = window.innerHeight;

                const start = rect.top - navHeight;
                const total = rect.height - vh;
                let p;

                if (rect.top > navHeight) p = 0;
                else if (rect.bottom <= navHeight + vh) p = 1;
                else p = Math.min(1, Math.max(0, (-start) / total));

                const idx = Math.min(slides.length - 1, Math.floor(p * slides.length));
                setActiveIndex(idx);
            });
        };

        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });

        return () => {
            window.removeEventListener("resize", updateHeight);
            window.removeEventListener("scroll", onScroll);
            cancelAnimationFrame(rafRef.current);
        };
    }, [slides.length, navHeight, spacerExtra]);

    return (
        <div ref={wrapperRef} className="relative w-full">
            {/* Sticky Full-Screen Container */}
            <div
                className="sticky flex flex-col items-center justify-center text-center "
                style={{
                    top: navHeight,
                    height: `calc(100vh - ${navHeight}px)`,
                    backgroundColor: "#f7b9c4",
                    color: "#0000ff",
                }}
            >
                {/* Title (Fixed for all slides) */}
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mt-16 leading-relaxed tracking-wider">
                    Daily practices to improve mindfulness
                </h1>

                {/* Image + Caption */}
                <div className="flex-1 flex flex-col items-center justify-center w-full relative">
                    {slides.map((s, i) => (
                        <div
                            key={s.key ?? i}
                            className="absolute inset-0 flex flex-col items-center justify-center transition-all duration-500"
                            style={{
                                opacity: i === activeIndex ? 1 : 0,
                                transform: `translateY(${i === activeIndex ? "0px" : "20px"})`,
                                pointerEvents: i === activeIndex ? "auto" : "none",
                            }}
                        >
                            <img
                                src={s.image}
                                alt={s.title}
                                className="max-h-[65vh] w-auto object-contain select-none"
                                draggable="false"
                            />
                            <p className="text-lg sm:text-xl font-semibold max-w-3xl leading-relaxed px-6 mb-10 tracking-wider">
                                {s.caption}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
